import { ArgValidator, Logger, Utils, ApiClientCommunication } from "neo-common";
import { ClientHost, Communication, GameEngine } from "neo-games";
import { GameManager } from "./GameManager";
import { AppContext } from "../../Common/AppContext";
import { Controller } from "../GameComponents/Controller";
import { UserControls } from "../Channels/Common/UserControls/UserControls";
import { DataManager } from "./DataManager";

var GameModules = Communication.Game.GameModules;
var GameStatuses = Communication.Game.GameModuleStatus;

export class ModuleManager {
    private static _gameModules: Object = {};
    private static _channelModules: Object = {};

    static registerGameModule(key: string, module: Controller): void {
        if (DEBUG) {
            ModuleManager.log("registerGameModule " + key);
        }

        if (ModuleManager._gameModules[key] == null) {
            ModuleManager._gameModules[key] = module;
        } else {
            if (DEBUG) {
                ModuleManager.log("ERROR try to register game module with same key: " + key);
            }
        }
    }

    static registerChannelModule(key: string, module: UserControls): void {
        if (DEBUG) {
            ModuleManager.log("registerChannelModule " + key);
        }

        if (ModuleManager._channelModules[key] == null) {
            ModuleManager._channelModules[key] = module;
        } else {
            if (DEBUG) {
                ModuleManager.log("ERROR try to register channel module with same key: " + key);
            }
        }
    }

    static connectModules(): boolean {
        if (DEBUG) {
            ModuleManager.log("connectModules");
        }

        let allChannelsHaveController: boolean = true;

        for (let key in ModuleManager._channelModules) {
            if (ModuleManager._gameModules[key] != null) {
                ModuleManager._channelModules[key].addController(ModuleManager._gameModules[key]);
                if (DEBUG) {
                    ModuleManager.log("connect module key: " + key);
                }
            } else {
                if (DEBUG) {
                    ModuleManager.log("cant connect module key: " + key);
                }
                allChannelsHaveController = false;
            }
        }

        return allChannelsHaveController;
    }

    static initModules(): void {
        if (DEBUG) {
            ModuleManager.log("initModules");
        }

        for (let key in ModuleManager._channelModules) {
            ModuleManager._channelModules[key].initReceived();
        }

        if (ModuleManager.gambleActive() && ModuleManager._channelModules[GameModules.Gamble] != null) {
            ModuleManager._channelModules[GameModules.Gamble].initIntro();
        } else if (ModuleManager.freespinsActive() && ModuleManager._channelModules[GameModules.Freespins] != null) {
            ModuleManager._channelModules[GameModules.Freespins].initIntro();
        } else {
            if (ModuleManager._channelModules[GameModules.Base] != null) {
                ModuleManager._channelModules[GameModules.Base].initIntro();
            }
        }
    }

    static gambleEnabled(): boolean {
        if (!AppContext.gameDefinition.Gamble) {
            return false;
        }

        if (DataManager.Autoplay && !AppContext.gameConfiguration.AutoplayGambleEnabled) {
            return false;
        }

        if (GameManager.gambleResponse != null && GameManager.gambleResponse.status === GameStatuses.Enabled) {
            return true;
        }
        return false;
    }

    static gambleActive(): boolean {
        if (!AppContext.gameDefinition.Gamble) {
            return false;
        }

        if (GameManager.gambleResponse != null && GameManager.gambleResponse.status === GameStatuses.Active) {
            return true;
        }
        return false;
    }

    static freespinsEnabled(): boolean {
        if (GameManager.freespinsResponse != null && GameManager.freespinsResponse.status === GameStatuses.Enabled) {
            DataManager.AutoplayCount = "∞";
            DataManager.AutoplayLossLimit = "∞";
            DataManager.AutoplayWinLimit = "∞";
            return true;
        }
        return false;
    }

    static freespinsActive(): boolean {
        if (GameManager.freespinsResponse != null && GameManager.freespinsResponse.status === GameStatuses.Active) {
            DataManager.AutoplayCount = "∞";
            DataManager.AutoplayLossLimit = "∞";
            DataManager.AutoplayWinLimit = "∞";
            return true;
        }
        return false;
    }

    static enableGamble(): void {
        if (DEBUG) {
            ModuleManager.log("enableGamble");
        }

        if (ModuleManager.gambleEnabled() && ModuleManager._channelModules[GameModules.Gamble] != null) {
            ModuleManager._channelModules[GameModules.Gamble].enable();
        }
    }

    static startGamble(): void {
        if (DEBUG) {
            ModuleManager.log("startGamble");
        }

        if (ModuleManager.gambleActive()) {
            ModuleManager.outroModule(GameModules.Base);
        }
    }

    static enableFreespins(): void {
        if (DEBUG) {
            ModuleManager.log("enableFreespins");
        }

        if (ModuleManager._channelModules[GameModules.Freespins] != null) {
            ModuleManager._channelModules[GameModules.Freespins].enable();
        }
    }

    static startFreespins(): void {
        if (DEBUG) {
            ModuleManager.log("startFreespins");
        }

        if (ModuleManager._channelModules[GameModules.Freespins] != null) {
            ModuleManager._channelModules[GameModules.Freespins].start();
        }
    }

    static endFreespins(): void {
        if (DEBUG) {
            ModuleManager.log("endFreespins");
        }

        if (ModuleManager._channelModules[GameModules.Freespins] != null) {
            ModuleManager._channelModules[GameModules.Freespins].end();
        }
    }

    static disableGamble(): void {
        if (DEBUG) {
            ModuleManager.log("disableGamble");
        }

        if (ModuleManager._channelModules[GameModules.Gamble] != null) {
            ModuleManager._channelModules[GameModules.Gamble].disable();
        }
    }

    static disableBase(): void {
        if (DEBUG) {
            ModuleManager.log("disableBase");
        }

        if (ModuleManager._channelModules[GameModules.Base] != null) {
            ModuleManager._channelModules[GameModules.Base].disable();
        }
    }

    /**
     * Disable channel module
     * @param key
     */
    static disableChannelModule(key: string): void {
        if (DEBUG) {
            ModuleManager.log("disable" + key);
        }

        if (ModuleManager._channelModules[key] != null) {
            ModuleManager._channelModules[key].disable();
        }
    }

    static changeBetColor(green: boolean = false): void {
        if (ModuleManager._channelModules[GameModules.Base] != null) {
            ModuleManager._channelModules[GameModules.Base].changeBetColor(green);
        }
    }

    static initNewBet(animate: boolean = true): void {
        if (ModuleManager._channelModules[GameModules.Base] != null) {
            ModuleManager._channelModules[GameModules.Base].initNewBet(animate);
        }
    }

    static introModule(key: string, long: boolean = false): void {
        if (DEBUG) {
            ModuleManager.log("introModule key: " + key);
        }

        if (ModuleManager._channelModules[key] != null) {
            ModuleManager._channelModules[key].intro(long);
        }
    }

    static outroModule(key: string): void {
        if (DEBUG) {
            ModuleManager.log("outroModule key: " + key);
        }

        if (ModuleManager._channelModules[key] != null) {
            ModuleManager._channelModules[key].outro();
        }
    }

    static collect(key: string): void {
        if (key != null && ModuleManager._channelModules[key] != null) {
            ModuleManager._channelModules[key].collect();
        }
    }

    static collectInModule(key: string): void {
        if (key != null && ModuleManager._channelModules[key] != null) {
            ModuleManager._channelModules[key].collectReceived();
        }
    }

    static restoreGameRound(key: string, collect: boolean = false): void {
        if (key != null && ModuleManager._channelModules[key] != null) {
            ModuleManager._channelModules[key].restoreGameRound(collect);
        }
    }

    static dispatchAction(action: string): void {
        if (DEBUG) {
            ModuleManager.log("dispatchAction action: " + action);
        }

        for (let key in ModuleManager._channelModules) {
            ModuleManager._channelModules[key].dispatchAction(action);
        }
    }

    static cycleHelp(): void {
        if (ModuleManager.freespinsActive() && ModuleManager._channelModules[GameModules.Freespins] != null) {
            ModuleManager._channelModules[GameModules.Freespins].cycleHelp();
        } else {
            if (ModuleManager._channelModules[GameModules.Base] != null) {
                ModuleManager._channelModules[GameModules.Base].cycleHelp();
            }
        }
    }

    /**
     * Set autoplay in Base module to true/false.
     * @param {boolean} tf
     */
    static setAutoplayAfterFeature(tf: boolean): void {
        if (DEBUG) {
            ModuleManager.log("setAutoplayAfterFeature to " + tf);
        }

        if (ModuleManager._channelModules[GameModules.Base] != null) {
            ModuleManager._channelModules[GameModules.Base].setAutoplayAfterFeature(tf);
        }
    }

    static primaryGameEnded(): boolean {
        if (ModuleManager._channelModules[GameModules.Base] != null) {
            return ModuleManager._channelModules[GameModules.Base].primaryGameEnded();
        }
    }

    static toggleModuleSounds(key: string, enable: boolean): void {
        if (ModuleManager._gameModules[key] != null) {
            ModuleManager._gameModules[key].toggleSounds(enable);
        }
    }

    static resetSceneAsOnCollect(key: string): void {
        if (ModuleManager._gameModules[key] != null) {
            ModuleManager._gameModules[key].collect();
        }
    }

    static resetWinAnimator(key: string): void {
        if (DEBUG) {
            ModuleManager.log("resetWinAnimator:" + key);
        }

        if (ModuleManager._channelModules[key] != null) {
            ModuleManager._channelModules[key].resetWinAnimator();
        }
    }

    static getActiveGameModules(): Array<string> {
        let activeModulesArray: Array<string> = [];
        if (GameManager.gameResponse != null && GameManager.gameResponse.modules != null) {
            for (var key in GameManager.gameResponse.modules) {
                if (GameManager.gameResponse.modules[key].status !== GameStatuses.Disabled) {
                    activeModulesArray.push(key);
                }
            }
        }
        return activeModulesArray;
    }

    private static log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup("ModuleManager", s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Managers/ModuleManager.ts
 **/